/**
 * Listing Buyability Check
 * Determines whether a listing can be purchased on-platform.
 * Shared utility used by conversion-button, product-detail, wishlist, and chat moderation.
 */

import { NON_SHIPABLE_CATS, NON_SHIPABLE_SUBCATS } from '@/generated/static-categories';
import { DATA_SOURCE } from '@/data/js/data-sources';

export const SELF_IMPOSED_UPPER_LIMIT = 9999999;

/** Data source IDs for external/partner listings that can't be bought on-platform */
export const EXTERNAL_DATA_SOURCES = [27, 28, 31, 32, 33, 35]; // GCC, MyEV, LotLinx, Amazon, BargainHunter, SportServer

export interface ListingBuyabilityData {
  shippable: number | string | null | undefined;
  categoryGroupId: number | string | null | undefined;
  categoryId: number | string | null | undefined;
  dataSourceId: number | string | null | undefined;
  price: number | string | null | undefined;
  status: number | string | null | undefined;
  sellerTrustLevel: number | null | undefined;
}

/**
 * Check if the price is over the platform buy-now threshold.
 */
export function isOverPriceThreshold(price: number | string | null | undefined): boolean {
  const p = price != null ? parseFloat(String(price)) : 0;
  return p >= SELF_IMPOSED_UPPER_LIMIT;
}

/**
 * Check if a listing is non-shippable based on category or shippable flag.
 */
export function isNonShippable(listing: {
  categoryGroupId: number | string | null | undefined;
  categoryId: number | string | null | undefined;
  shippable?: number | string | null | undefined;
} | null | undefined): boolean {
  if (!listing) return false;
  const groupId = listing.categoryGroupId != null ? parseInt(String(listing.categoryGroupId)) : null;
  const catId = listing.categoryId != null ? parseInt(String(listing.categoryId)) : null;

  if (groupId != null && NON_SHIPABLE_CATS.includes(groupId)) return true;
  if (catId != null && NON_SHIPABLE_SUBCATS.includes(catId)) return true;
   
  if (listing.shippable == 0 && listing.shippable != null) return true;

  return false;
}

/**
 * Check if a listing can be purchased on-platform.
 * Returns false when users need to exchange contact info to complete the transaction.
 */
export function isListingBuyableOnPlatform(listing: ListingBuyabilityData): boolean {
  const dataSourceId = listing.dataSourceId != null ? parseInt(String(listing.dataSourceId)) : null;

  // External data sources are never buyable on-platform
  if (dataSourceId != null && EXTERNAL_DATA_SOURCES.includes(dataSourceId)) {
    return false;
  }
  if (dataSourceId === DATA_SOURCE.RESCUEGROUPS) {
    return false;
  }

  // Price over threshold
  if (isOverPriceThreshold(listing.price)) {
    return false;
  }

  // Non-shippable items
  if (isNonShippable(listing)) {
    return false;
  }

  // Unverified seller
  const trustLevel = listing.sellerTrustLevel != null ? Number(listing.sellerTrustLevel) : null;
  if (trustLevel == null || trustLevel < 2) {
    return false;
  }

  return true;
}
